/**
 * Alarm toasts, and the notification bell that keeps what you missed.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE THEY APPEAR, AND WHY IT IS NOT THE TOP RIGHT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Bottom right. The top right of this console is where the site selector, the compliance
 * chip, the bell and the account menu live, and a toast stack there covers the controls an
 * operator reaches for WHEN AN ALARM ARRIVES — including the bell that would let them see
 * the rest of them. An interruption that hides the response to itself is the wrong
 * interruption.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ACCESSIBILITY IS NOT OPTIONAL HERE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * This is a live region, so a screen reader has to be told. `role="log"` with
 * `aria-live="polite"` announces arrivals without cutting off whatever is being read —
 * `assertive` would interrupt mid-sentence, and on a chattering device that is unusable.
 *
 * The countdown is paused on hover and on focus. A toast that vanishes while somebody is
 * reading it, or while a keyboard user is tabbing to its dismiss button, is a toast that
 * cannot be acted on.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon';
import { TOAST_MS, useAlarmFeed, type ToastItem } from '@/app/alarm-feed';
import type { LiveAlarm } from '@/lib/api/events';
import { chime, shouldChime, shouldToast, useNotifyPrefs } from '@/app/notify-prefs';

/* --------------------------------------------------------------- severity */

/**
 * Tone by severity, with the EVENT overriding it.
 *
 * A cleared critical is good news and must not be painted like a critical fault — the
 * colour has to say what happened, not only what kind of thing it was.
 */
function tone(a: LiveAlarm): { colour: string; rgb: string; icon: 'ic-alert' | 'ic-check' | 'ic-info' } {
  if (a.event === 'closed') return { colour: 'var(--success)', rgb: 'var(--success-rgb)', icon: 'ic-check' };
  /* A reminder asks for a person, whatever the alarm's own severity: one colour, the warning one. */
  if (a.event === 'reminder') return { colour: 'var(--warn)', rgb: 'var(--warn-rgb)', icon: 'ic-info' };
  const s = a.severity.toLowerCase();
  if (s.includes('critical')) return { colour: 'var(--danger)', rgb: 'var(--danger-rgb)', icon: 'ic-alert' };
  if (s.includes('major') || s.includes('warn')) return { colour: 'var(--warn)', rgb: 'var(--warn-rgb)', icon: 'ic-alert' };
  return { colour: 'var(--info)', rgb: 'var(--info-rgb)', icon: 'ic-info' };
}

const VERB: Record<LiveAlarm['event'], string> = {
  opened: 'raised',
  reopened: 're-raised',
  closed: 'cleared',
  changed: 'updated',
  reminder: 'reminder',
};

/** A reminder's heading, by its rule (Lumen-BE 0091): what is asked of the reader. */
const reminderHead = (a: LiveAlarm): string => (a.rule === 'untouched' ? 'Waiting on you' : 'No owner yet — you are the shift lead');

function ago(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

/* ----------------------------------------------------------------- toasts */

export function AlarmToasts(): ReactNode {
  const all = useAlarmFeed((s) => s.toasts);
  const dismiss = useAlarmFeed((s) => s.dismiss);
  /* The reader's own choice on this device (`notify-prefs.ts`). Filtering what is DRAWN, not what
     is stored: the bell and the board still hold every event. Defaults show everything, silently. */
  const level = useNotifyPrefs((p) => p.toasts);
  const sound = useNotifyPrefs((p) => p.sound);
  const toasts = useMemo(() => all.filter((t) => shouldToast(t, level)), [all, level]);

  /* One chime per ARRIVAL, keyed like the toasts are, so a re-render never sounds twice. */
  const heard = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const t of all) {
      if (heard.current.has(t.key)) continue;
      heard.current.add(t.key);
      if (shouldChime(t, { sound })) chime();
    }
    if (heard.current.size > 200) heard.current = new Set(all.map((t) => t.key));
  }, [all, sound]);

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      style={{ width: 380, maxWidth: 'calc(100vw - 32px)' }}
      role="log"
      aria-live="polite"
      aria-label="Alarm notifications"
    >
      {/* Newest at the BOTTOM, nearest the corner: the stack grows upward, so the position
          of the newest item does not move as others arrive. A stack that grows downward
          moves the thing you are reading. */}
      {/*
        `dismiss` IS PASSED WHOLE, not wrapped. `onDismiss={() => dismiss(t.key)}` builds a
        fresh closure on every render of this component, and that closure is in the child's
        effect dependency array — so every store update (any toast arriving, any toast
        leaving) cleared and re-armed the timeout on EVERY visible toast. On a device
        chattering faster than TOAST_MS, nothing ever auto-dismissed and the stack grew until
        it ran off the screen. There is no React Compiler in this build to memoise it away.
        The store action is stable, so the child's effect now depends only on things that
        genuinely change.
      */}
      {toasts.map((t) => (
        <Toast key={t.key} item={t} onDismiss={dismiss} />
      ))}
    </div>
  );
}

function Toast({
  item,
  onDismiss,
}: {
  item: ToastItem;
  /** The store action itself — stable across renders. See the note at the call site. */
  onDismiss: (key: string) => void;
}): ReactNode {
  const [paused, setPaused] = useState(false);
  const content = useNotifyPrefs((p) => p.content);
  const t = tone(item);
  const life = TOAST_MS[item.event];

  /*
   * The timer restarts whenever `paused` goes false, which is the intended behaviour: a
   * toast the operator hovered gets its full life again when they move away, rather than
   * disappearing the instant the cursor leaves.
   */
  useEffect(() => {
    if (paused || life <= 0) return;
    const id = setTimeout(() => onDismiss(item.key), life);
    return () => clearTimeout(id);
  }, [paused, life, item.key, onDismiss]);

  return (
    <div
      className="lm-overlay lm-slidein pointer-events-auto flex items-start gap-[10px] p-[12px_13px]"
      style={{ borderLeft: `3px solid ${t.colour}` }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span
        className="mt-[1px] flex size-[22px] shrink-0 items-center justify-center rounded-[var(--radius-sm)]"
        style={{ background: `rgb(${t.rgb} / 16%)`, color: t.colour }}
      >
        <Icon name={t.icon} size={13} />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[12.5px] font-bold" style={{ color: t.colour }}>
            {item.event === 'reminder' ? reminderHead(item) : `${item.severity} ${VERB[item.event]}`}
          </span>
          <span className="lm-num text-[11px]" style={{ color: 'var(--muted-fg)' }}>
            {ago(item.receivedAt)}
          </span>
        </div>
        {/*
          The design's "Show content of notification". Off, the toast still says WHAT happened
          and how bad — the headline above — and withholds WHERE, which is the part somebody
          sharing a screen may not want on it. It is never the whole toast that is hidden: an
          alarm that arrived silently and invisibly is an alarm nobody was told about.
        */}
        {content && (
          <>
            <div className="mt-[2px] truncate text-[12.5px]" style={{ color: 'var(--fg)' }}>
              {item.name}
            </div>
            <div className="truncate text-[11.5px]" style={{ color: 'var(--muted-fg)' }}>
              {item.device}
            </div>
          </>
        )}
      </div>

      <button
        type="button"
        onClick={() => onDismiss(item.key)}
        className="lm-hov shrink-0 rounded-[var(--radius-sm)] p-1"
        style={{ color: 'var(--muted-fg)' }}
        aria-label="Dismiss"
      >
        <Icon name="ic-x" size={13} />
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------- bell */

/**
 * The header bell: an unseen count, and the log behind it.
 *
 * The count is UNSEEN rather than unread — it clears when the panel is opened, not when
 * each row is looked at. Per-row read state on a notification log is a promise the product
 * would then have to keep across devices and sessions, and nobody wants it: the question
 * the badge answers is "has anything happened since I last looked".
 */
export function AlarmBell(): ReactNode {
  const { recent, unseen, markSeen, stream } = useAlarmFeed();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  /*
   * The design's "Show bell icon on System Bar" and its unread indicator. Both are this
   * device's (`app/notify-prefs.ts`), and both are read here rather than passed in, because the
   * header has no business knowing why the bell is or is not drawn.
   *
   * Hiding the bell hides the LOG, not the alarms: they go on being raised, counted, listed on
   * the Alarms board and escalated by mail exactly as before. Somebody who turns it off has said
   * they watch the board instead, not that they want less to happen.
   */
  const showBell = useNotifyPrefs((p) => p.bell);
  const badge = useNotifyPrefs((p) => p.badge);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const label = useMemo(() => {
    if (stream === 'offline') return 'Alarm stream offline — the board still refreshes on its own';
    if (stream === 'connecting') return 'Connecting to the alarm stream…';
    return unseen > 0 ? `${unseen} new alarm event(s)` : 'Live — no new alarm events';
  }, [stream, unseen]);

  /* After the hooks, never before them: an early return above `useMemo` would change the hook
     order the moment somebody switched the bell off. */
  if (!showBell) return null;

  return (
    <div className="relative" ref={wrap}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (!open) markSeen();
        }}
        className="lm-hov relative flex items-center justify-center rounded-[var(--radius-sm)]"
        style={{
          width: 'var(--header-ctl-h)',
          height: 'var(--header-ctl-h)',
          color: 'var(--muted-fg)',
        }}
        aria-label={label}
        title={label}
        aria-expanded={open}
      >
        <Icon name="ic-bell" size={16} />

        {unseen > 0 &&
          (badge === 'dot' ? (
            <span
              className="absolute -right-[1px] -top-[1px] size-[7px] rounded-full"
              style={{ background: 'var(--danger)' }}
              aria-hidden="true"
            />
          ) : (
            <span
              className="lm-num absolute -right-[1px] -top-[1px] flex min-w-[16px] items-center justify-center rounded-[var(--radius-pill)] px-[3px] text-[9.5px] font-bold"
              style={{ background: 'var(--danger)', color: 'var(--primary-fg)' }}
            >
              {unseen > 99 ? '99+' : unseen}
            </span>
          ))}

        {/*
          * THE STREAM STATE IS SHOWN, and that matters more than it looks.
          *
          * If the stream is down the operator is looking at a bell that will never light
          * up, and the honest thing is to say so — otherwise silence means "nothing is
          * wrong" when it means "we are not being told". The dot is the only place in the
          * UI that distinguishes the two.
          */}
        {unseen === 0 && (
          <span
            className="absolute bottom-[6px] right-[6px] size-[5px] rounded-full"
            style={{
              background:
                stream === 'live'
                  ? 'var(--success)'
                  : stream === 'connecting'
                    ? 'var(--warn)'
                    : 'var(--danger)',
            }}
          />
        )}
      </button>

      {open && (
        <div
          className="lm-overlay absolute right-0 top-[calc(100%+8px)] z-50 overflow-hidden"
          style={{ width: 380, maxWidth: 'calc(100vw - 24px)' }}
        >
          <div
            className="flex items-center justify-between px-[13px] py-[9px]"
            style={{ borderBottom: '1px solid var(--glass-border)' }}
          >
            <span className="text-[12px] font-bold uppercase tracking-[0.08em]">
              Recent alarm activity
            </span>
            <span
              className="flex items-center gap-1.5 text-[11px]"
              style={{
                color:
                  stream === 'live'
                    ? 'var(--success)'
                    : stream === 'connecting'
                      ? 'var(--warn)'
                      : 'var(--danger)',
              }}
            >
              <span
                className="size-[6px] rounded-full"
                style={{ background: 'currentColor' }}
              />
              {stream}
            </span>
          </div>

          <div className="max-h-[380px] overflow-y-auto">
            {recent.length === 0 ? (
              <p className="px-[13px] py-[14px] text-[12px]" style={{ color: 'var(--muted-fg)' }}>
                {stream === 'live'
                  ? 'Nothing since this page was opened. This log starts empty on every load — it is what has happened while you were watching, not the alarm history.'
                  : 'Not connected, so nothing is being pushed. The Alarms page still refreshes on its own.'}
              </p>
            ) : (
              recent.map((a, i) => {
                const t = tone(a);
                return (
                  <div
                    key={`${a.id}-${a.event}-${a.receivedAt}-${i}`}
                    className="flex items-start gap-[9px] px-[13px] py-[8px]"
                    style={{ borderBottom: '1px solid var(--glass-border)' }}
                  >
                    <span
                      className="mt-[3px] size-[7px] shrink-0 rounded-full"
                      style={{ background: t.colour }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[12px]" style={{ color: 'var(--fg)' }}>
                        {a.name}
                      </div>
                      <div className="truncate text-[11px]" style={{ color: 'var(--muted-fg)' }}>
                        {a.device} · {VERB[a.event]} · {ago(a.receivedAt)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
